import { useEffect, useState } from "react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useRecoilValue } from "recoil";
import { ANONYMOUS_LD_CONTEXT_KEY, FeatureFlags } from "../launchDarklyUtils";
import {
  ldIdentifyPendingState,
  ldSyncedContextKeyState,
  patientState,
} from "../../recoil/atoms";
import { useBooleanFeatureFlag } from "./useFeatureFlag";

export const STANDALONE_FETAL_SEX_EXPERIENCE = {
  LOADING: "loading",
  LEGACY: "legacy",
  STANDALONE: "standalone",
} as const;

export type StandaloneFetalSexExperience =
  (typeof STANDALONE_FETAL_SEX_EXPERIENCE)[keyof typeof STANDALONE_FETAL_SEX_EXPERIENCE];

const LEGACY_FALLBACK_TIMEOUT_MS = 5000;

/**
 * Resolve the percentage-based standalone fetal-sex rollout once per page.
 * Pinning the first resolved value prevents a live flag update from changing
 * the meaning of an interaction that is already in progress.
 */
export const useStandaloneFetalSexRollout =
  (): StandaloneFetalSexExperience => {
    const flags = useFlags();
    const ldClient = useLDClient();
    const patientInfo = useRecoilValue(patientState);
    const ldSyncedContextKey = useRecoilValue(ldSyncedContextKeyState);
    const isLdIdentifyPending = useRecoilValue(ldIdentifyPendingState);

    // Preserve the standard patient feature-flag analytics used elsewhere in
    // the portal. The stable experience below intentionally reads the SDK value
    // directly so it does not inherit the base hook's one-render state lag.
    useBooleanFeatureFlag(FeatureFlags.PatientStandaloneFetalSex);

    // identify() is asynchronous, so right after an SPA login the SDK still
    // serves the previous context's values (the shared "anonymous" key). A
    // value only counts as resolved once the client evaluates the identity
    // this session renders for; otherwise the wrong identity's experience
    // would be pinned for the rest of the page lifetime.
    const expectedLdContextKey =
      patientInfo?.auth0.user_id ?? ANONYMOUS_LD_CONTEXT_KEY;
    const activeLdContext = ldClient?.getContext();
    const activeLdContextKey =
      activeLdContext && "key" in activeLdContext
        ? activeLdContext.key
        : undefined;
    // identify() assigns the client's context inside the flag-fetch
    // continuation, so getContext() reports the new key slightly before the new
    // values reach React. Trust it only when no switch is in flight; during a
    // switch the settled synced key is the sole evidence that the flags on hand
    // belong to this identity.
    const isContextSynced =
      ldSyncedContextKey === expectedLdContextKey ||
      (!isLdIdentifyPending && activeLdContextKey === expectedLdContextKey);

    const isResolved =
      isContextSynced &&
      Object.prototype.hasOwnProperty.call(
        flags,
        FeatureFlags.PatientStandaloneFetalSex,
      );
    const resolvedExperience =
      flags[FeatureFlags.PatientStandaloneFetalSex] === true
        ? STANDALONE_FETAL_SEX_EXPERIENCE.STANDALONE
        : STANDALONE_FETAL_SEX_EXPERIENCE.LEGACY;
    const [experience, setExperience] = useState<StandaloneFetalSexExperience>(
      () =>
        isResolved
          ? resolvedExperience
          : STANDALONE_FETAL_SEX_EXPERIENCE.LOADING,
    );

    useEffect(() => {
      if (experience !== STANDALONE_FETAL_SEX_EXPERIENCE.LOADING) return;

      if (isResolved) {
        setExperience(resolvedExperience);
        return;
      }

      const fallbackTimer = window.setTimeout(() => {
        setExperience(STANDALONE_FETAL_SEX_EXPERIENCE.LEGACY);
      }, LEGACY_FALLBACK_TIMEOUT_MS);

      return () => window.clearTimeout(fallbackTimer);
    }, [experience, isResolved, resolvedExperience]);

    return experience;
  };
